/**
 * The overview's numbers, derived on the phone like the log's (its local day):
 * day-by-day totals, bottles by hour of the day, noon-to-noon sleep rows, the right-now box score, and the
 * "needs a look" checks for entries that may have been forgotten (decided in
 * @babia/checks, worded here). Pure, so every threshold is unit-tested.
 */
import {
  type BottleUnit,
  findForgotten,
  formatAmount,
  fromOz,
  HISTORY_DAYS,
  localCalendar,
  MAX_PLAUSIBLE_SLEEP_MS,
  roundOz,
  snapOz,
  type TrackableType,
} from "@babia/checks";
import type { EntryLike, EntryType } from "./entry-text.ts";
import {
  addLocalDays,
  ago,
  agoText,
  clock,
  dur,
  isSameLocalDay,
  minutesText,
  pluralize,
  startOfLocalDay,
  weekdayDay,
} from "./time.ts";

export { HISTORY_DAYS, MAX_PLAUSIBLE_SLEEP_MS };

const MIN = 60_000;
const HOUR = 60 * MIN;

/**
 * The live log query covers 48h and polls every 5s; the history query covers
 * two weeks and refreshes rarely. Below this age history is used, above it the
 * live list (so a deletion a minute ago never lingers from a stale history).
 */
const LIVE_COVERS_MS = 47 * HOUR;

/** Live entries for the recent past, history for the rest; newest first. */
export function mergeHistory<T extends { id: string; at: Date }>(
  live: readonly T[] | undefined,
  history: readonly T[] | undefined,
  now: Date,
): T[] {
  const recent = live ?? [];
  if (!history) return [...recent];
  if (!live) return [...history];
  const cutoff = now.getTime() - LIVE_COVERS_MS;
  const liveIds = new Set(recent.map((e) => e.id));
  const older = history.filter((e) => e.at.getTime() < cutoff && !liveIds.has(e.id));
  return [...recent, ...older].sort((a, b) => b.at.getTime() - a.at.getTime());
}

const endOf = (e: EntryLike, now: Date) => (e.endedAt ?? now).getTime();
const overlap = (a0: number, a1: number, b0: number, b1: number) =>
  Math.max(0, Math.min(a1, b1) - Math.max(a0, b0));
const isDirty = (e: EntryLike) => e.diaperKind === "dirty" || e.diaperKind === "both";
const isWet = (e: EntryLike) => e.diaperKind === "wet" || e.diaperKind === "both";

/**
 * The first local day worth charting: two weeks back, or the household's first
 * entry if that's later, so a new household doesn't see a row of empty days.
 * Shared by every baby so twins' charts line up.
 */
export function chartStart(entries: readonly EntryLike[], now: Date): Date {
  const earliest = addLocalDays(now, -(HISTORY_DAYS - 1));
  let first = now.getTime();
  for (const e of entries) first = Math.min(first, e.at.getTime());
  return first > earliest.getTime() ? startOfLocalDay(new Date(first)) : earliest;
}

// --- Day totals ------------------------------------------------------------

/**
 * Amounts are stored ounces whatever unit the household logs in; sums stay
 * in ounces (`roundOz`) and the screen converts (`fromOz`), so a fortnight of
 * millilitre bottles adds up to the millilitres the parents typed. A bottle
 * logged in the other unit is shown rounded on its own, so a day's shown
 * total can sit a millilitre off the sum of its shown bottles.
 */
export interface DayTotals {
  /** Local midnight. */
  day: Date;
  isToday: boolean;
  oz: number;
  bottles: number;
  /** Each bottle's ounces, oldest first; null for a bottle logged without them. */
  bottleOz: (number | null)[];
  changes: number;
  /** Wet changes, a change that is both included. */
  wet: number;
  /** Dirty changes, a change that is both included. */
  dirty: number;
  /** Changes both wet and dirty. */
  both: number;
  /** Time asleep inside this local day (naps across midnight are split). */
  sleepMs: number;
}

/** One row per local day from `from` through today, oldest first. */
export function dailyTotals(
  entries: readonly EntryLike[],
  babyId: string,
  now: Date,
  from: Date,
): DayTotals[] {
  const mine = entries.filter((e) => e.babyId === babyId);
  const out: DayTotals[] = [];
  for (let day = startOfLocalDay(from); day <= now; day = addLocalDays(day, 1)) {
    const d0 = day.getTime();
    const d1 = addLocalDays(day, 1).getTime();
    const t: DayTotals = {
      day,
      isToday: isSameLocalDay(day, now),
      oz: 0,
      bottles: 0,
      bottleOz: [],
      changes: 0,
      wet: 0,
      dirty: 0,
      both: 0,
      sleepMs: 0,
    };
    const feeds: EntryLike[] = [];
    for (const e of mine) {
      const at = e.at.getTime();
      if (e.type === "sleep") {
        t.sleepMs += overlap(at, Math.min(endOf(e, now), now.getTime()), d0, d1);
        continue;
      }
      if (at < d0 || at >= d1) continue;
      if (e.type === "feed") {
        t.bottles += 1;
        t.oz += e.amountOz ?? 0;
        feeds.push(e);
      } else if (e.type === "diaper") {
        t.changes += 1;
        if (isWet(e)) t.wet += 1;
        if (isDirty(e)) t.dirty += 1;
        if (e.diaperKind === "both") t.both += 1;
      }
    }
    t.bottleOz = feeds
      .sort((a, b) => a.at.getTime() - b.at.getTime())
      .map((e) => e.amountOz ?? null);
    t.oz = roundOz(t.oz);
    out.push(t);
  }
  return out;
}

/** Average of a daily number over complete days (today's partial day excluded). */
export function dailyAverage(days: readonly DayTotals[], pick: (d: DayTotals) => number) {
  const done = days.filter((d) => !d.isToday);
  if (!done.length) return null;
  return done.reduce((n, d) => n + pick(d), 0) / done.length;
}

/** A round ceiling for a shared scale, in at most ten steps: 23 → 25, 7 → 7, 705 → 800, 0 → 1. */
export function niceMax(v: number): number {
  if (v <= 0) return 1;
  const step = [1, 2, 5, 10, 20, 50, 100, 200, 500].find((s) => v / s <= 10) ?? 1000;
  return Math.ceil(v / step) * step;
}

// --- Bottle blocks ----------------------------------------------------------

/** The chart key for a day's nth bottle (0-based): "b0", "b1", … */
export const bottleKey = (n: number) => `b${n}`;

/**
 * The most bottles in one day across every baby's days, so each twin's chart
 * stacks the same series. At least 1, so an empty chart still has a series.
 */
export function bottleDepth(perBaby: readonly (readonly DayTotals[])[]): number {
  let depth = 1;
  for (const days of perBaby) for (const d of days) depth = Math.max(depth, d.bottleOz.length);
  return depth;
}

/**
 * One row per day for the bottle-blocks chart: `day` names it, and `b0` up to
 * `b{depth-1}` hold each bottle's amount in `unit`, oldest first. A slot is
 * null past the day's last bottle and for a bottle logged without an amount,
 * so it draws no block.
 */
export function bottleRows(
  days: readonly DayTotals[],
  depth: number,
  unit: BottleUnit,
): Record<string, string | number | null>[] {
  return days.map((d) => {
    const row: Record<string, string | number | null> = { day: weekdayDay(d.day) };
    for (let n = 0; n < depth; n++) {
      const oz = d.bottleOz[n];
      row[bottleKey(n)] = oz === undefined || oz === null ? null : fromOz(oz, unit);
    }
    return row;
  });
}

// --- Change kinds -----------------------------------------------------------

/**
 * One row per day for the Changes chart: `day` names it, and `wet`, `both`, and
 * `dirty` count the changes of each kind, so the kinds stack in their own
 * colors. A kind with no changes that day is null, so it draws no segment.
 */
export function changeRows(days: readonly DayTotals[]): Record<string, string | number | null>[] {
  return days.map((d) => ({
    day: weekdayDay(d.day),
    wet: d.wet - d.both || null,
    both: d.both || null,
    dirty: d.dirty - d.both || null,
  }));
}

// --- Bottles by hour (noon to noon) ------------------------------------------

/** Clock hours in chart order, noon first, so the night is one block in the middle. */
const HOUR_ORDER = Array.from({ length: 24 }, (_, i) => (i + 12) % 24);

/** One clock hour on the bottles-by-hour chart, over the counted days. */
export interface HourRow {
  /** The clock hour (`getHours()`): 18 holds 6:00 to 6:59 pm. */
  hour: number;
  bottles: number;
  /** Stored ounces; a bottle without an amount adds none. */
  oz: number;
  /** Ounces a bottle, as in the report; null with no bottles. */
  avgOz: number | null;
}

/** One baby's bottles by hour of the day. */
export interface BabyHours {
  /** Counted days: a bottle logged, not today, not the day logging began. */
  days: number;
  bottles: number;
  oz: number;
  avgOz: number | null;
  /** All 24 hours, noon first: index 0 is the 12 pm hour, index 12 the 12 am hour. */
  rows: HourRow[];
}

/**
 * When the household began logging, so its partial first day can be left out.
 * Once the milestones load, the earlier of their `trackingSince` (each baby's
 * first entry ever, of the types still tracked) and the first loaded entry
 * (any type, so a type turned off since still counts). Until then, the
 * report's rule (`reportPeriod`) over `chartStart`'s window: the first loaded
 * entry if it is inside the window, else null. History loads a day further
 * back than the charts show, so that guess is wrong only for a household that
 * logged nothing on that day.
 */
export function loggingBegan(
  entries: readonly EntryLike[],
  now: Date,
  trackingSince: readonly (Date | null)[] = [],
): Date | null {
  let loaded: Date | null = null;
  for (const e of entries) if (!loaded || e.at < loaded) loaded = e.at;
  let known: Date | null = null;
  for (const at of trackingSince) if (at && (!known || at < known)) known = at;
  if (known) return loaded && loaded < known ? loaded : known;
  return loaded && loaded >= addLocalDays(now, -(HISTORY_DAYS - 1)) ? loaded : null;
}

/**
 * The first local midnight at or after `t`: where the full days of a list
 * that reaches back to `t` begin. For the live 48 hours that is yesterday,
 * or today when yesterday had 25 hours (the clocks fell back).
 */
export function firstFullDay(t: Date): Date {
  const day = startOfLocalDay(t);
  return day < t ? addLocalDays(day, 1) : day;
}

/**
 * One baby's bottles in each hour of the day, from `from` up to today: which
 * hours the baby feeds most. Only counted days, as in the report: today isn't
 * over and the day logging began (`began`) is partial, so both are left out. A
 * bottle without ounces counts as a bottle at 0 oz, as in `dailyTotals`. Hours
 * are the phone's wall clock, so the night the clocks fall back puts two real
 * hours in the 1 am bar.
 */
export function bottleHours(
  entries: readonly EntryLike[],
  babyId: string,
  now: Date,
  from: Date,
  began: Date | null,
): BabyHours {
  const start = startOfLocalDay(from).getTime();
  const end = startOfLocalDay(now).getTime();
  const bottles = Array<number>(24).fill(0);
  const oz = Array<number>(24).fill(0);
  const days = new Set<string>();
  for (const e of entries) {
    if (e.babyId !== babyId || e.type !== "feed") continue;
    const at = e.at.getTime();
    if (at < start || at >= end || (began && isSameLocalDay(e.at, began))) continue;
    const hour = e.at.getHours();
    bottles[hour] = bottles[hour]! + 1;
    oz[hour] = oz[hour]! + (e.amountOz ?? 0);
    days.add(localCalendar.isoDate(e.at));
  }
  const total = { bottles: 0, oz: 0 };
  const rows = HOUR_ORDER.map((hour): HourRow => {
    const n = bottles[hour]!;
    total.bottles += n;
    total.oz += oz[hour]!;
    return { hour, bottles: n, oz: roundOz(oz[hour]!), avgOz: n ? roundOz(oz[hour]! / n) : null };
  });
  return {
    days: days.size,
    bottles: total.bottles,
    oz: roundOz(total.oz),
    avgOz: total.bottles ? roundOz(total.oz / total.bottles) : null,
    rows,
  };
}

/** The hour with the most bottles, then the most ounces, then the first from noon; null with none. */
export function busiestHour(rows: readonly HourRow[]): HourRow | null {
  let best: HourRow | null = null;
  for (const r of rows) {
    if (!r.bottles) continue;
    if (!best || r.bottles > best.bottles || (r.bottles === best.bottles && r.oz > best.oz))
      best = r;
  }
  return best;
}

/** "3 bottles, 7 oz (2.3 oz a bottle)" in the household's `unit`, or "1 bottle, no amount logged", or "no bottles". */
export function hourText(
  r: { bottles: number; oz: number; avgOz: number | null },
  unit: BottleUnit,
): string {
  if (!r.bottles) return "no bottles";
  const count = pluralize(r.bottles, "bottle", "bottles");
  if (!r.oz || r.avgOz === null) return `${count}, no amount logged`;
  return `${count}, ${formatAmount(r.oz, unit)} (${formatAmount(r.avgOz, unit)} a bottle)`;
}

// --- Sleep rows (noon to noon) --------------------------------------------

export interface SleepSegment {
  id: string;
  /** Fractions of the row, 0 at noon to 1 at the next noon. */
  start: number;
  end: number;
  open: boolean;
  /** Part of the row's longest stretch: the night's main sleep. */
  main: boolean;
}

export interface SleepRow {
  /** Local noon the row starts at; the row is "the night of" this date. */
  start: Date;
  end: Date;
  segments: SleepSegment[];
  /** The longest stretch that began in this row (an open nap counts so far). */
  longest: { id: string; at: Date; endedAt: Date | null; ms: number } | null;
  /** Time asleep inside the row. */
  totalMs: number;
  /** Where "now" falls in the current row; null for past rows. */
  nowAt: number | null;
}

const noonOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12);

/**
 * Where an instant sits along a noon-to-noon row, by the wall clock: 6 pm is
 * always a quarter of the way along, even across a 23- or 25-hour DST night,
 * so bars line up with the chart's fixed noon / 6 pm / 12 am / 6 am ticks.
 */
function clockPosition(t: number, rowStart: Date): number {
  const d = new Date(t);
  const days = Math.round(
    (startOfLocalDay(d).getTime() - startOfLocalDay(rowStart).getTime()) / (24 * HOUR),
  );
  const hours = days * 24 + d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600 - 12;
  return Math.min(1, Math.max(0, hours / 24));
}

/**
 * Sleep laid out noon to noon, newest row first, so a night reads as one
 * unbroken bar and the nightly window can be compared down the column.
 */
export function sleepRows(
  entries: readonly EntryLike[],
  babyId: string,
  now: Date,
  from: Date,
): SleepRow[] {
  const sleeps = entries.filter((e) => e.babyId === babyId && e.type === "sleep");
  const latest = now.getHours() >= 12 ? noonOf(now) : noonOf(addLocalDays(now, -1));
  // The row holding `from`'s morning starts the previous noon.
  const first = noonOf(addLocalDays(from, -1));
  const rows: SleepRow[] = [];
  for (let start = latest; start >= first; start = noonOf(addLocalDays(start, -1))) {
    const end = noonOf(addLocalDays(start, 1));
    const r0 = start.getTime();
    const r1 = end.getTime();
    const current = now.getTime() < r1;

    let longest: SleepRow["longest"] = null;
    for (const e of sleeps) {
      const at = e.at.getTime();
      const ms = endOf(e, now) - at;
      if (at < r0 || at >= r1 || ms > MAX_PLAUSIBLE_SLEEP_MS) continue;
      if (!longest || ms > longest.ms) longest = { id: e.id, at: e.at, endedAt: e.endedAt, ms };
    }

    let totalMs = 0;
    const segments: SleepSegment[] = [];
    for (const e of sleeps) {
      const s0 = Math.max(e.at.getTime(), r0);
      const s1 = Math.min(endOf(e, now), r1, now.getTime());
      if (s1 <= s0) continue;
      totalMs += s1 - s0;
      const startAt = clockPosition(s0, start);
      segments.push({
        id: e.id,
        start: startAt,
        // The repeated hour when clocks fall back can put the end "before" it.
        end: Math.max(startAt, clockPosition(s1, start)),
        open: e.endedAt === null,
        main: e.id === longest?.id,
      });
    }
    segments.sort((a, b) => a.start - b.start);
    // Skip the leading empty row a morning-only first day would add.
    if (start < from && !segments.length) continue;
    rows.push({
      start,
      end,
      segments,
      longest,
      totalMs,
      nowAt: current ? clockPosition(now.getTime(), start) : null,
    });
  }
  return rows;
}

// --- Right now (the box score) --------------------------------------------

export interface Glance {
  /** Asleep since the open nap began, awake since the last one ended. */
  state: { asleep: boolean; since: Date } | null;
  lastFeed: EntryLike | null;
  lastDiaper: EntryLike | null;
  /** Stored ounces, to show in the household's unit. */
  todayOz: number;
  todayBottles: number;
  /** Ounces logged by this time of day on average over the past week, snapped to the unit's step. */
  typicalOzByNow: number | null;
  todayChanges: number;
  todayDirty: number;
  sleepLast24Ms: number;
  longestLast24: { at: Date; endedAt: Date | null; ms: number } | null;
  /** Medicine names logged today, first given first. */
  medsToday: string[];
}

export function glance(
  entries: readonly EntryLike[],
  babyId: string,
  now: Date,
  unit: BottleUnit,
): Glance {
  const mine = entries
    .filter((e) => e.babyId === babyId)
    .sort((a, b) => b.at.getTime() - a.at.getTime());
  const today = mine.filter((e) => isSameLocalDay(e.at, now));
  const lastOf = (type: EntryType) => mine.find((e) => e.type === type) ?? null;
  const sleeps = mine.filter((e) => e.type === "sleep");
  const open = sleeps.find((e) => e.endedAt === null);
  const lastEnded = sleeps
    .filter((e) => e.endedAt !== null)
    .sort((a, b) => b.endedAt!.getTime() - a.endedAt!.getTime())[0];

  const n = now.getTime();
  let sleepLast24Ms = 0;
  let longestLast24: Glance["longestLast24"] = null;
  for (const e of sleeps) {
    const ms = endOf(e, now) - e.at.getTime();
    sleepLast24Ms += overlap(e.at.getTime(), Math.min(endOf(e, now), n), n - 24 * HOUR, n);
    if (endOf(e, now) > n - 24 * HOUR && ms <= MAX_PLAUSIBLE_SLEEP_MS)
      if (!longestLast24 || ms > longestLast24.ms)
        longestLast24 = { at: e.at, endedAt: e.endedAt, ms };
  }

  const feedsToday = today.filter((e) => e.type === "feed");
  const changesToday = today.filter((e) => e.type === "diaper");
  return {
    state: open
      ? { asleep: true, since: open.at }
      : lastEnded
        ? { asleep: false, since: lastEnded.endedAt! }
        : null,
    lastFeed: lastOf("feed"),
    lastDiaper: lastOf("diaper"),
    todayOz: roundOz(feedsToday.reduce((s, e) => s + (e.amountOz ?? 0), 0)),
    todayBottles: feedsToday.length,
    typicalOzByNow: typicalOzByNow(mine, now, unit),
    todayChanges: changesToday.length,
    todayDirty: changesToday.filter(isDirty).length,
    sleepLast24Ms,
    longestLast24,
    medsToday: [
      ...new Set(
        today
          .filter((e) => e.type === "meds" && e.label)
          .reverse()
          .map((e) => e.label!),
      ),
    ],
  };
}

/**
 * "Usually 16 oz by now": ounces logged between midnight and this clock time,
 * averaged over the past week's days that had any bottle and snapped to the
 * unit's step (a half ounce, or 10 ml). Needs two such days.
 */
export function typicalOzByNow(
  mine: readonly EntryLike[],
  now: Date,
  unit: BottleUnit,
): number | null {
  const feeds = mine.filter((e) => e.type === "feed");
  const totals: number[] = [];
  for (let k = 1; k <= 7; k++) {
    const day = addLocalDays(now, -k);
    const next = addLocalDays(day, 1);
    const cutoff = new Date(
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      now.getHours(),
      now.getMinutes(),
    );
    const ofDay = feeds.filter((e) => e.at >= day && e.at < next);
    if (!ofDay.length) continue;
    totals.push(ofDay.filter((e) => e.at < cutoff).reduce((s, e) => s + (e.amountOz ?? 0), 0));
  }
  if (totals.length < 2) return null;
  return snapOz(totals.reduce((s, v) => s + v, 0) / totals.length, unit);
}

// --- Needs a look ----------------------------------------------------------

export interface Attention {
  /** Stable React key. */
  key: string;
  /** Which check found it ("open-nap", "overdue", …). */
  kind: ReturnType<typeof findForgotten>[number]["kind"];
  babyId: string;
  title: string;
  detail: string;
}

interface BabyRef {
  id: string;
  name: string;
}

const NOUN: Record<"feed" | "diaper", string> = { feed: "bottle", diaper: "change" };

/**
 * Entries that look forgotten (`findForgotten`, shared with the MCP tools),
 * worded for the phone: local clock times and the tile a parent taps. Types
 * the household turned off (`untracked`) raise nothing.
 */
export function needsALook(
  entries: readonly EntryLike[],
  babies: readonly BabyRef[],
  now: Date,
  untracked: readonly TrackableType[] = [],
): Attention[] {
  const nameOf = (id: string) => babies.find((b) => b.id === id)?.name ?? "";
  return findForgotten(entries, babies, now, localCalendar, untracked).map((f) => {
    const name = nameOf(f.babyId);
    const text = (title: string, detail: string) => ({
      key: f.key,
      kind: f.kind,
      babyId: f.babyId,
      title,
      detail,
    });
    switch (f.kind) {
      case "open-nap":
        return text(
          `${name}'s nap from ${clock(f.nap.at)} is still running`,
          `A ${NOUN[f.after.type as "feed" | "diaper"]} was logged at ${clock(f.after.at)}, after it started. Forgot to tap Wake up?`,
        );
      case "long-nap":
        return text(
          `${name} has been asleep ${ago(f.nap.at, now)}`,
          f.longestMs
            ? `Longer than any stretch logged lately (${minutesText(Math.round(f.longestMs / MIN))}). Still asleep? Then it's a new record. If not, tap Wake up.`
            : "Still asleep? If not, tap Wake up.",
        );
      case "twin":
        return text(
          `${nameOf(f.sibling.babyId)} had a ${NOUN[f.type]} ${agoText(f.sibling.last.at, now)}`,
          `Nothing for ${name} since ${clock(f.last.at)}. They're usually done together — logged both?`,
        );
      case "overdue":
        return text(
          `No ${NOUN[f.type]} logged for ${name} in ${ago(f.last.at, now)}`,
          `${f.type === "feed" ? "Bottles have" : "Changes have"} been about ${minutesText(f.usualGapMin)} apart lately. Missed logging one?`,
        );
      case "meds":
        return text(
          `${f.label} not logged for ${name} today`,
          `Usually given by ${clock(f.usualAt)}.`,
        );
      case "sleep-too-long":
        return text(
          `A ${dur(f.sleep.at, f.sleep.endedAt!)} sleep is logged for ${name}`,
          `From ${weekdayDay(f.sleep.at)} at ${clock(f.sleep.at)} — probably a missed Wake up tap. Remove it from the log, or ask your assistant to fix the wake time.`,
        );
    }
  });
}
